/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-24
 */

// Add third-party dependencies.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Email } from '@mui/icons-material';
import { useLocation, Link } from 'react-router-dom';

// Add local dependencies.
import { useEmailVerificationRequestMutation } from '../../../features/auth';
import TurnstileWidget from '../Waitlist/waitlist/TurnstileWidget';

// Define component props interface.
interface LocationState {
  email?: string;
}

/**
 * Email verification pending component.
 * @example
 * return (
 *   <EmailVerificationPending />
 * )
 * @returns {React.ReactElement} component.
 */
const EmailVerificationPending: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  // Get email from location state
  const location = useLocation();
  const state = location.state as LocationState;
  const email = state?.email || '';

  // Component state
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');

  // API hooks
  const [resendVerification, { isLoading }] =
    useEmailVerificationRequestMutation();

  // Handle resend verification email
  const handleResendEmail = async () => {
    if (!email) {
      setResendError('Email address not found. Please register again.');
      return;
    }

    if (!turnstileToken) {
      setResendError('Please complete the verification challenge.');
      return;
    }

    try {
      await resendVerification({
        email,
        turnstile_token: turnstileToken,
        language: i18n.language, // Send current i18next language (en/es/fr)
      }).unwrap();
      setResendSuccess(true);
      setResendError('');
    } catch (error: any) {
      setResendError(
        error.data?.message ||
          'Failed to resend verification email. Please try again.'
      );
      setResendSuccess(false);
    }
  };

  // Render component.
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            textAlign: 'center',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Email sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              {t('emailVerification.pending.title')}
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('emailVerification.pending.sentTo')}
          </Typography>

          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
            {email}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            {t('emailVerification.pending.instructions')}
          </Typography>

          {resendSuccess && (
            <Alert severity="success" sx={{ mb: 2, width: '100%' }}>
              {t('emailVerification.pending.success.emailSent')}
            </Alert>
          )}

          {resendError && (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
              {resendError}
            </Alert>
          )}

          <TurnstileWidget
            onSuccess={setTurnstileToken}
            onError={() =>
              setResendError('Verification failed. Please try again.')
            }
            onExpire={() => setTurnstileToken('')}
            disabled={isLoading}
          />

          <Button
            variant="outlined"
            onClick={handleResendEmail}
            disabled={isLoading || !email || !turnstileToken}
            sx={{ mb: 3 }}
          >
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              t('emailVerification.pending.button.resend')
            )}
          </Button>

          <Typography variant="body2" color="text.secondary">
            {t('emailVerification.pending.links.alreadyVerified')}{' '}
            <Link
              to="/login"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              {t('emailVerification.pending.links.signIn')}
            </Link>
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailVerificationPending;
